import type { CriterionCard, ProtocolStepDraft } from '@matter_extractor/shared';

/**
 * The two seams of the protocol editor (refonte modulaire §7; maquette
 * `editeur-protocole.html`, screen 3): what the FRISE and the STEP EDITOR are
 * handed by the page, and nothing else.
 *
 * The editor of a protocol is three surfaces built by three hands - the page
 * and its shell (`protocol-editor-fields.tsx`), the frise and the tree
 * (`timeline/`), the step's own panels (`step/`) - and this file is the
 * agreement between them. Both components are CONTROLLED: the page holds the
 * draft and the selection, the two surfaces render them and ask for a change
 * through a callback. Neither reads a query, neither writes the draft itself.
 *
 * `data-testid` marks are declared here for the same reason the versioned
 * layer puts its prefixes in `VersionedWording`: the e2e spec and the capture
 * script read them, and a mark spelled in two components would be a selector
 * that resolves in one and not the other.
 */

/* ------------------------------------------------------------- the frise */

export interface ProtocolTimelineProps {
  /** Every step of the draft, in order. */
  readonly steps: readonly ProtocolStepDraft[];
  /** The step whose jumps are drawn and whose card is pressed. Always one of `steps`. */
  readonly selectedStepId: string;
  /** « Voir » or a retired protocol: no insertion, no drag, no trash. */
  readonly readOnly: boolean;
  readonly onSelect: (stepId: string) => void;
  /** The « + » at slot `index`: a new step goes at that index (0 = before the first). */
  readonly onInsert: (index: number) => void;
  /**
   * A drag-and-drop landed: the step at `fromIndex` moves so that it sits at
   * `toIndex` in the RESULTING list. Same contract as `moveStep` in
   * `lib/protocol-draft.ts`.
   */
  readonly onMove: (fromIndex: number, toIndex: number) => void;
  /** The trash on an EMPTY step's card (§7.1) - no confirmation, the step holds nothing. */
  readonly onRemoveEmpty: (stepId: string) => void;
  /**
   * The frise is on screen - false while the reading view of #154 holds the
   * place and the conduite is kept mounted, `hidden` (review of !474). Absent:
   * on screen. The centring of the selected card waits for it: a hidden track
   * measures zero, and a selection made meanwhile - « N problèmes » jumps to a
   * step from the reading - must be centred when the frise shows again.
   */
  readonly visible?: boolean;
}

/* ------------------------------------------------------- the step editor */

export interface StepEditorProps {
  /** Every step of the draft: the palette and the selects need the others. */
  readonly steps: readonly ProtocolStepDraft[];
  /** The selected step. Always one of `steps`. */
  readonly stepId: string;
  /** The criteria library, for names, pointers and example counts of the cited criteria. */
  readonly criteria: readonly CriterionCard[];
  /**
   * True once the library has been READ. A cited criterion absent from an
   * unread library is unknown, not gone: the row must not accuse before it
   * knows (the régime of `libraryRead` in the persona form).
   */
  readonly criteriaRead: boolean;
  readonly readOnly: boolean;
  /**
   * The step whose NAME field must take the focus and be selected, or `null`.
   *
   * §4.2 asks a duplication to select its copy « son champ de nom avec elle »,
   * and the copy is a step the PAGE selects - so the page is also what knows
   * that this selection came from a duplication rather than from a click on the
   * frise. An id rather than a boolean, so a selection that moves elsewhere
   * cannot be mistaken for this one.
   */
  readonly nameFocusId: string | null;
  /**
   * Said once the name field has actually taken the focus, so the page can
   * FORGET the request.
   *
   * Without it the request would outlive the gesture: select another step, come
   * back, and the remounted editor would grab the caret again on a screen the
   * operator was only reading. `useFocusOnce` holds the mechanism; this is the
   * half of it a contract has to state.
   */
  readonly onNameFocused: () => void;
  /** The step, changed: name, direction, objectives, rules or conditions. */
  readonly onChangeStep: (next: ProtocolStepDraft) => void;
  /** The step menu's « Déplacer à gauche / à droite ». */
  readonly onMoveStep: (by: -1 | 1) => void;
  /**
   * « Dupliquer » - the gutter's copy icon AND the step menu's entry (§4.2).
   *
   * Two triggers, one callback: they are the same gesture, and the menu entry
   * exists only because a menu is already there. The insertion, the naming and
   * the selection of the copy are the page's (`lib/duplication.ts`), because
   * none of the three can be decided from one step.
   */
  readonly onDuplicateStep: () => void;
  /**
   * The step menu's « Supprimer l'étape », AFTER the confirmation that counts
   * the inbound jumps (§7.1) - the confirmation is the step editor's own.
   */
  readonly onDeleteStep: () => void;
}

/* ------------------------------------------------------------ the marks */

/**
 * Every `data-testid` of the protocol editor that more than one file reads.
 *
 * Prefixed `protocole-`, like the versioned layer's `${entity}-...`, so the
 * spec and the capture script read one register. A mark used by a single
 * component may live in that component; the ones here are read across seams.
 */
export const PROTOCOL_TEST_IDS = {
  /* the frise (timeline/) */
  timeline: 'protocole-frise',
  viewTimeline: 'protocole-vue-frise',
  viewTree: 'protocole-vue-arbre',
  /** One per step card; carries `data-step-id` and `aria-pressed`. */
  stepCard: 'protocole-etape-carte',
  /** One per « + »; carries `data-slot` (the insertion index). */
  insertStep: 'protocole-inserer-etape',
  removeEmptyStep: 'protocole-etape-carte-supprimer',
  zoomOut: 'protocole-frise-zoom-moins',
  zoomIn: 'protocole-frise-zoom-plus',
  toggleLinks: 'protocole-frise-liens',
  toggleLabels: 'protocole-frise-etiquettes',
  toggleFollow: 'protocole-frise-suivre',
  /** One per drawn jump (an SVG group); carries `data-from` and `data-to`. */
  jump: 'protocole-saut',
  tree: 'protocole-arbre',
  /** One per step node of the tree; carries `data-step-id`. */
  treeNode: 'protocole-arbre-noeud',
  treeFold: 'protocole-arbre-replier',
  /**
   * The badge « adieu » of a farewell step (issue #163, §7.1), on its frise card
   * and on its node of the tree - one mark for the two, since a step is never
   * drawn by both at once.
   */
  farewellBadge: 'protocole-etape-badge-adieu',
  /** The leaf « fin du protocole » a farewell step ends its path on in the tree. */
  treeFarewellEnd: 'protocole-arbre-fin-adieu',

  /* the step editor (step/) */
  stepName: 'protocole-etape-nom',
  stepMenu: 'protocole-etape-menu',
  /** The list of the step's options, under its name (issue #163, variante A). */
  stepOptions: 'protocole-etape-options',
  /**
   * The checkbox « Étape d'adieu » itself (§7.2) - `.check()` it. It is
   * `disabled` when the step still carries objectives or conditions.
   */
  stepFarewell: 'protocole-etape-adieu',
  /** The dashed note that stands for the three blocks a farewell step hides. */
  stepFarewellHidden: 'protocole-etape-adieu-masques',
  /**
   * The checkbox « À l'entrée, relire l'étape d'où l'on vient » (issue #165,
   * §7.2), under the farewell box - `.check()` it. It is `disabled` on a
   * farewell step, which has no objectives to fill.
   */
  stepReread: 'protocole-etape-relecture',
  /**
   * The checkbox « Cacher les objectifs à l'interviewer » (issue #191, §4.3),
   * under the reread box - `.check()` it. It is `disabled` on a farewell step,
   * which has no objectives to hide.
   */
  stepHideObjectives: 'protocole-etape-objectifs-caches',
  /** The pill of an objective's direction: who reads it (H20, issue #191). */
  objectiveDirectionReader: 'protocole-objectif-lecteur',
  /**
   * The three copy icons of §4.2, one per duplicable item of this editor.
   *
   * They are marks of the CONTRACT and not of a component, for the reason the
   * head gives: the e2e spec of the duplication and the capture script of the
   * MR both aim at them, and the gesture is offered from two places on a step -
   * the gutter icon below and the « Dupliquer » entry of `stepMenu`, which is
   * clicked by its label.
   */
  duplicateStep: 'protocole-etape-dupliquer',
  duplicateObjective: 'protocole-objectif-dupliquer',
  duplicateCondition: 'protocole-condition-dupliquer',
  stepDirection: 'protocole-etape-direction',
  insertVariable: 'protocole-inserer-variable',
  palette: 'protocole-palette',
  paletteItem: 'protocole-palette-item',
  /** One per objective row; carries `data-objective-id` and `aria-expanded`. */
  objective: 'protocole-objectif',
  addObjective: 'protocole-ajouter-objectif',
  objectiveName: 'protocole-objectif-nom',
  objectiveMandatory: 'protocole-objectif-obligatoire',
  objectiveOptional: 'protocole-objectif-optionnel',
  objectiveDirection: 'protocole-objectif-direction',
  /** One per cited criterion; carries `data-criterion-id`. */
  criterionRow: 'protocole-critere-ligne',
  addCriterion: 'protocole-ajouter-critere',
  /** The chooser opened by « Ajouter un critère »; one `criterionChoice` per candidate. */
  criterionChooser: 'protocole-choix-critere',
  criterionChoice: 'protocole-choix-critere-item',
  criterionName: 'protocole-critere-nom',
  criterionVersion: 'protocole-critere-version',
  criterionThresholdAdd: 'protocole-critere-seuil-ajouter',
  /**
   * The pill that READS the threshold and toggles the panel: « seuil : plancher
   * 50 » in grey, « seuil 75 » in bleu nuit (§7.2, grill of 2026-09-16).
   *
   * It replaces the `criterionThresholdValue` number field of the first MR of
   * issue #81, which lived for one lot: a threshold is « calé sur un début de
   * tranche », and the tranches are the control.
   */
  criterionThreshold: 'protocole-critere-seuil',
  /** The slider panel, unfolded under the row and inside the open objective. */
  criterionThresholdPanel: 'protocole-critere-seuil-panneau',
  /** One per band start; carries `data-band-start` (the NUMBER that is stored). */
  criterionThresholdTick: 'protocole-critere-seuil-graduation',
  criterionThresholdPlancher: 'protocole-critere-seuil-plancher',
  /**
   * « Retirer le seuil ». ONE mark for the two places that offer the gesture -
   * the panel's own button and the cross beside the orphan pill - because a row
   * never shows both: the panel is mounted on a scored version, the pill says
   * there is none.
   */
  criterionThresholdRemove: 'protocole-critere-seuil-retirer',
  /** The yellow pill of H47: a threshold on a version that carries no notation. */
  criterionThresholdOrphan: 'protocole-critere-seuil-orphelin',
  criterionRemove: 'protocole-critere-retirer',
  /** The frame of a « l'un de » group of criteria (issue #181); carries `data-or-group`. */
  criterionGroup: 'protocole-critere-groupe',
  /** « + ou » on the junction of two lines: links them into one group. */
  criterionLink: 'protocole-critere-lier',
  /** « ou » between two members of a group, « ou × » hovered: cuts the group there. */
  criterionUnlink: 'protocole-critere-delier',
  /** « Délier », the way out of a group of a single criterion. */
  criterionGroupRelease: 'protocole-critere-groupe-delier',
  /** « Ajouter au groupe » at the foot of a group's frame. */
  criterionGroupAdd: 'protocole-critere-groupe-ajouter',
  /** The yellow refusal of a group of fewer than two criteria. */
  criterionGroupRefusal: 'protocole-critere-groupe-refus',
  quantitySentences: 'protocole-quantite-phrases',
  quantityChars: 'protocole-quantite-caracteres',
  deleteObjective: 'protocole-supprimer-objectif',
  ruleMaxTurns: 'protocole-regle-plafond',
  ruleEmptyAnswers: 'protocole-regle-vides',
  ruleAttempts: 'protocole-regle-essais',
  fallbackClose: 'protocole-repli-fermer',
  fallbackGoTo: 'protocole-repli-aller',
  fallbackText: 'protocole-repli-texte',
  fallbackTextDefault: 'protocole-repli-texte-defaut',
  fallbackTarget: 'protocole-repli-cible',
  rulesRecap: 'protocole-regles-recap',
  /** One per condition card; carries `data-condition-id`. */
  condition: 'protocole-condition',
  addCondition: 'protocole-ajouter-condition',
  trigger: 'protocole-declencheur',
  triggerOutcome: 'protocole-declencheur-statut',
  addTrigger: 'protocole-ajouter-declencheur',
  addGroup: 'protocole-ajouter-groupe',
  action: 'protocole-action',
  addAction: 'protocole-ajouter-action',
  removeCondition: 'protocole-supprimer-condition',
  deleteStepConfirm: 'protocole-etape-supprimer-confirmer',

  /* the page (protocol-editor-fields, header extras) */
  description: 'protocole-description',
  problems: 'protocole-problemes',
  problemsList: 'protocole-problemes-liste',
  openHistory: 'protocole-historique-ouvrir',
  drawer: 'protocole-tiroir',
  closeDrawer: 'protocole-tiroir-fermer',

  /*
    The section Analyste (reading/; analyste.md §3, issue #196) - what the view
    « Lecture pour l'analyste » of #154 and #158 became. Read by
    `e2e/tests/lecture-analyste.spec.ts` and `e2e/screenshots-section-analyste.mjs`,
    which recopy them: none compiles this package. A mark that has a natural
    successor keeps its value - the segments of the switch, the field of the first
    reading - so no selector written before #196 breaks on a screen that kept its
    element; what is mounted twice (the two readings) carries a DISTINCT value,
    the strict mode of Playwright refusing two matches.
  */
  /** The radiogroup of the switch « Conduite de l'entretien » / « Analyste » / « Rangement ». */
  viewSwitch: 'protocole-bascule',
  /** The first two radios of the switch (the third is `viewRangement`) - `.check()` them. */
  viewConduct: 'protocole-bascule-conduite',
  /** The segment « Analyste » - « Lecture pour l'analyste » before #196. */
  viewReading: 'protocole-bascule-lecture',
  /** The yellow point on that segment, present only while the section is incomplete (§3.5). */
  viewReadingMark: 'protocole-bascule-marque',
  /** The section: the levels, the numbers and the readings on the left, the panel of the pass on the right. */
  analyst: 'protocole-analyste',
  /** The yellow encart above the levels, present only while the section is incomplete. */
  analystEncart: 'protocole-analyste-encart',
  /** The group « Niveaux des objectifs »: one `analystLevelRow` per objective, grouped by step. */
  analystLevels: 'protocole-analyste-niveaux',
  /** One objective's row; carries `data-objective-id` and `data-level` (`none`, `why`, `how` or `what`). */
  analystLevelRow: 'protocole-analyste-niveau',
  /** One radio of a row - `.check()` it; carries `data-level` (`none`, `why`, `how` or `what`). */
  analystLevelChoice: 'protocole-analyste-niveau-choix',
  /** The line of the counts, « Pourquoi : 2 objectifs · Comment : 2 · Quoi : 2 ». */
  analystCounts: 'protocole-analyste-comptes',
  /** The pastille beside it, green or yellow; carries `data-state` (`complete` or `incomplete`). */
  analystPastille: 'protocole-analyste-pastille',
  /** The group « Certitude, poids et lectures » and its four numbers. */
  analystSettings: 'protocole-analyste-reglages',
  analystPairThreshold: 'protocole-analyste-seuil-paire',
  analystSoloThreshold: 'protocole-analyste-seuil-seul',
  analystStyleWeight: 'protocole-analyste-poids-style',
  /** « Lectures du style » (#229): how many times the second pass is read. */
  analystStyleRuns: 'protocole-analyste-lectures-style',
  /** The `<output>` under a number that does not read; carries `data-field`. */
  analystNumberRefusal: 'protocole-analyste-refus-nombre',
  /** The tablist « Passe 1 · les fonctions » / « Passe 2 · le style », and its two tabs. */
  analystTabs: 'protocole-analyste-onglets',
  analystTabPass1: 'protocole-analyste-onglet-passe-1',
  analystTabPass2: 'protocole-analyste-onglet-passe-2',
  /** The panel « Ce que la passe N recevra » of the ACTIVE tab; carries `data-pass`. */
  analystPreview: 'protocole-analyste-apercu',
  /** The line under it that says what the message of that pass carries. */
  analystMessage: 'protocole-analyste-message',
  /** The badge of the part Méthode wrote - « Réglages > Méthode vN »; carries `data-state` (`read`, `reading`, `missing` or `failed`). */
  analystMethodBadge: 'protocole-analyste-methode',
  /** The grey block « Lecture d'avant #196 (plus servie) », its toggle and « Reprendre dans la passe 1 ». */
  analystPrevious: 'protocole-analyste-avant',
  analystPreviousToggle: 'protocole-analyste-avant-bascule',
  analystPreviousTake: 'protocole-analyste-avant-reprendre',
  /**
   * The field of each reading - the painted field of the directions since #158
   * (N55), a `role="textbox"` whose objectives are pills: read its TEXT, not a
   * value. The first is the field of the reading of before, whose value it keeps.
   */
  readingField: 'protocole-lecture-champ',
  styleReadingField: 'protocole-lecture-passe-2-champ',
  /** The `{ }` button of each field (#158, N55). */
  readingInsertVariable: 'protocole-lecture-inserer-variable',
  styleReadingInsertVariable: 'protocole-lecture-passe-2-inserer-variable',
  /** The palette of a reading: the objectives of every step, grouped by step (N49). */
  readingPalette: 'protocole-lecture-palette',
  styleReadingPalette: 'protocole-lecture-passe-2-palette',
  /** « Lecture générique, préremplie » - under a field that still holds its template. */
  readingGeneric: 'protocole-lecture-generique',
  styleReadingGeneric: 'protocole-lecture-passe-2-generique',
  /**
   * The `<output>` (role `status`) under a field that says what its bound
   * refused (second review of !478): present whenever the field is typable,
   * empty until a refusal, emptied by the next change the field takes.
   */
  readingRefused: 'protocole-lecture-refus',
  styleReadingRefused: 'protocole-lecture-passe-2-refus',
  /** One of the four parts of the panel, with `data-kind` (framing, method, reading, contract) and `data-open`. */
  readingPart: 'protocole-lecture-partie',
  /** The protocol's own part, framed in violet - dashed, with `data-empty="true"`, when there is none. */
  readingServed: 'protocole-lecture-texte',
  /**
   * The yellow encart of the save box and of the pointer box that names the
   * experiences a version WITH AN INCOMPLETE SECTION would stop from starting
   * (analyste.md §3.7, N37); one `readingWarningLine` per experience, with
   * `data-experience-id`. Since #158 the same encart carries the orphan chips of
   * the version (N52), one `readingOrphanLine` each - `data-kind` `reading` (with
   * `data-pass`, since #196), `direction` or, since #177, `rangement`, and
   * `data-step-id` on a direction's - the title counting them all.
   */
  readingWarning: 'protocole-boite-lecture',
  readingWarningLine: 'protocole-boite-lecture-ligne',
  readingOrphanLine: 'protocole-boite-orpheline',

  /*
    The view « Rangement » (scenario-succes.md §4, issue #177): ids ONLY, the
    view is the lot of the editor's. It is the third radio of the switch, after
    `viewConduct` and `viewReading`.
  */
  /** The third radio of the switch - `.check()` it. */
  viewRangement: 'protocole-bascule-rangement',
  /** The view « Rangement »: the targets and the consigne on the left, the panel on the right. */
  rangement: 'protocole-rangement',
  /** The list of the objectives that can be ticked as targets, grouped by step. */
  rangementTargets: 'protocole-rangement-cibles',
  /** One checkbox of it, with `data-objective-id`. */
  rangementTarget: 'protocole-rangement-cible',
  /**
   * The checkbox « Un passage ne sert qu'un objectif » (relecture.md §12, issue
   * #187), under the targets and unticked by default: `rangement.oneObjectivePerPassage`.
   */
  rangementOnePassage: 'protocole-rangement-un-objectif',
  /** The field « Consigne du rangeur » - a painted field like the reading's: read its TEXT. */
  rangementConsigne: 'protocole-rangement-consigne',
  /** The `{ }` button of the consigne's field. */
  rangementInsertVariable: 'protocole-rangement-inserer-variable',
  /** The palette `@` opens in the consigne: every named objective, grouped by step. */
  rangementPalette: 'protocole-rangement-palette',
  /** The panel « Ce que le rangeur recevra ». */
  rangementPanel: 'protocole-rangement-apercu',
  /**
   * One of its five parts, with `data-kind` (framing, targets, consigne,
   * conversation, contract) and `data-open` - the first four of the system, in
   * the order `rangerSystemParts` assembles them, the conversation (the user
   * message) drawn between the consigne and the contract, as the maquette does.
   */
  rangementPart: 'protocole-rangement-partie',
  /** The targets part's body: the list as the system serves it, `data-empty` while none is ticked. */
  rangementTargetsServed: 'protocole-rangement-cibles-texte',
  /** The consigne as served - resolved, trimmed - with `data-empty`: read its TEXT. */
  rangementConsigneServed: 'protocole-rangement-consigne-texte',
  /** The `<output>` (role `status`) under the consigne that says what its bound refused. */
  rangementRefused: 'protocole-rangement-consigne-refus',
  /**
   * The yellow line of a refusal or of an orphan chip, under the field it
   * concerns: `data-kind` is `rangement-consigne-missing` (under the consigne),
   * `rangement-targets-missing` (under the targets) or `rangement-variable-orphan`
   * (under the consigne).
   */
  rangementWarning: 'protocole-rangement-avertissement',
} as const;
